import React from "react";
import {
  ActivityIndicator,
  Pressable,
  PressableProps,
  StyleProp,
  StyleSheet,
  Text,
  TextProps,
  View,
  ViewProps,
  ViewStyle,
} from "react-native";

import { tPressableState, tSvgProps } from "../types";
import {
  ButtonContext,
  tPdButtonColorScheme,
  tPdButtonSize,
  tPdButtonVariant,
  useButtonLabelStyles,
  useButtonStyles,
} from "./hooks";

export type tPdBaseButton = {
  isDisabled?: boolean;
  isLoading?: boolean;
  isFullWidth?: boolean;
  leftIcon?: React.ReactElement<tSvgProps>;
  rightIcon?: React.ReactElement<tSvgProps>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  pressed: boolean;
  hovered?: boolean;
  focused?: boolean;
} & ViewProps;

export type tPdButtonProps = {
  isDisabled?: boolean;
  isLoading?: boolean;
  isFullWidth?: boolean;
  leftIcon?: React.ReactElement<tSvgProps>;
  rightIcon?: React.ReactElement<tSvgProps>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  colorScheme?: tPdButtonColorScheme;
  size?: tPdButtonSize;
  variant?: tPdButtonVariant;
} & PressableProps &
  ViewProps;

const BaseButton: React.FC<tPdBaseButton> = ({
  isDisabled,
  isLoading,
  isFullWidth,
  leftIcon,
  rightIcon,
  pressed,
  hovered,
  focused,
  children,
  contentContainerStyle,
  ...rest
}) => {
  const buttonStyles = useButtonStyles(isDisabled || isLoading);
  const buttonLabelStyles = useButtonLabelStyles();
  const { variant } = React.useContext(ButtonContext);

  const isLink = variant === "link";

  return (
    <View style={[styles.pdButton, buttonStyles, contentContainerStyle]} {...rest}>
      {!!leftIcon && (!isLoading || isLink) && (
        <View style={styles.pdButtonLeftIcon}>
          {React.cloneElement(leftIcon, { color: buttonLabelStyles.color, style: leftIcon.props.style })}
        </View>
      )}
      {isLoading && !isLink && (
        <View style={styles.pdButtonLeftIcon}>
          <ActivityIndicator color={buttonLabelStyles.color} />
        </View>
      )}
      {children}
      {isLoading && isLink && (
        <View style={[styles.pdButtonLeftIcon, styles.mlAuto]}>
          <ActivityIndicator color={buttonLabelStyles.color} />
        </View>
      )}
      {!!rightIcon && (!isLoading || isLink) && (
        <View style={styles.pdButtonRightIcon}>
          {React.cloneElement(rightIcon, { color: buttonLabelStyles.color, style: rightIcon.props.style })}
        </View>
      )}
    </View>
  );
};

export const PdButton: React.FC<tPdButtonProps> = ({
  isFullWidth,
  isDisabled,
  isLoading,
  leftIcon,
  colorScheme = "green",
  size = "md",
  variant = "solid",
  onPress,
  style,
  ...rest
}) => {
  return (
    <Pressable disabled={isDisabled || isLoading} onPress={onPress} style={[isFullWidth ? styles.wFull : {}, style]}>
      {({ pressed, hovered, focused }: tPressableState) => (
        <ButtonContext.Provider value={{ colorScheme, size, variant, pressed, hovered, focused }}>
          <BaseButton {...{ isDisabled, isLoading, isFullWidth, leftIcon, pressed, hovered, focused }} {...rest} />
        </ButtonContext.Provider>
      )}
    </Pressable>
  );
};

export const PdButtonLabel: React.FC<TextProps> = ({ style, ...rest }) => {
  const buttonLabelStyles = useButtonLabelStyles();
  return <Text style={[styles.pdButtonLabel, buttonLabelStyles, style]} {...rest} />;
};

const styles = StyleSheet.create({
  mlAuto: {
    marginLeft: "auto",
  },
  pdButton: {
    alignItems: "center",
    borderWidth: 2,
    flexDirection: "row",
    justifyContent: "center",
    minWidth: 150,
  },
  pdButtonLabel: {
    fontFamily: "Raleway",
    fontWeight: "600",
    letterSpacing: 1.1,
    textAlign: "center",
    textTransform: "uppercase",
    userSelect: "none",
  },
  pdButtonLeftIcon: {
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
  },
  pdButtonRightIcon: {
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 10,
  },
  wFull: {
    width: "100%",
  },
});
